import React, { useState } from 'react'
import moment from 'moment'
import { Message } from './types'
import VoiceMemoCard from './voice-memo-card'
import ImageAttachmentCard from './image-attachment-card'
import VideoAttachmentCard from './video-attachment-card'
import ContactCardAttachment from './contact-card-attachment'
import FileAttachmentCard from './file-attachment-card'
import ImageModal from './image-modal'

interface AttachmentMessageCardProps {
  message: Message
  showTimestamp: boolean
  getStatusIcon: (message: Message) => JSX.Element | string
  formatTimestamp: (timestamp: string) => string
  isGroupChat?: boolean
  isFirstInGroup?: boolean
}

const AttachmentMessageCard: React.FC<AttachmentMessageCardProps> = ({
  message,
  showTimestamp,
  getStatusIcon,
  formatTimestamp,
  isGroupChat = false,
  isFirstInGroup = false,
}) => {
  const [selectedImage, setSelectedImage] = useState<{
    url: string
    filename?: string
  } | null>(null)

  return (
    <div>
      {showTimestamp && (
        <div className="text-center mb-3">
          <small className="text-white px-2 py-1 rounded">
            {formatTimestamp(message.sent_at)}
          </small>
        </div>
      )}

      {/* Sender name for group messages */}
      {isGroupChat && isFirstInGroup && !message.is_from_me && (
        <div
          className={`mb-1 d-flex ${
            !message.is_from_me
              ? 'justify-content-start'
              : 'justify-content-end'
          }`}
        >
          <div
            style={{
              maxWidth: '70%',
              fontSize: 'clamp(11px, 2.5vw, 13px)',
              color: 'rgba(255, 255, 255, 0.7)',
              marginBottom: '2px',
              fontWeight: '500',
              paddingLeft: '12px',
            }}
          >
            {message.sender?.display_name ||
              message.sender?.identifier ||
              'Unknown'}
          </div>
        </div>
      )}

      <div
        className={`mb-3 ${!message.is_from_me ? 'text-start' : 'text-end'}`}
      >
        {/* Attachment Cards */}
        {message.attachments?.map((attachment, index) => {
          const isVoiceMemo =
            attachment.metadata?.type === 'voice_memo' ||
            attachment.mime_type?.startsWith('audio/')

          return (
            <div
              key={attachment.id}
              className={`d-inline-block ${
                !isVoiceMemo ? 'rounded-3 py-2' : ''
              } ${index > 0 || message.text ? 'mt-2' : ''}`}
              style={{
                maxWidth: '70%',
                color: !message.is_from_me ? '#000f30' : '#ffffff',
                display: 'block',
              }}
            >
              <div>
                {attachment.mime_type?.startsWith('image/') ? (
                  <ImageAttachmentCard
                    attachment={attachment}
                    setSelectedImage={setSelectedImage}
                  />
                ) : attachment.mime_type?.startsWith('video/') &&
                  attachment.metadata?.type !== 'voice_memo' ? (
                  <VideoAttachmentCard attachment={attachment} />
                ) : attachment.metadata?.type === 'voice_memo' ? (
                  <VoiceMemoCard
                    attachment={attachment}
                    isFromMe={message.is_from_me}
                    service={message.service}
                  />
                ) : attachment.mime_type?.startsWith('audio/') ? (
                  <VoiceMemoCard
                    attachment={attachment}
                    isFromMe={message.is_from_me}
                    service={message.service}
                  />
                ) : attachment.mime_type === 'text/vcard' ||
                  attachment.filename?.endsWith('.vcf') ? (
                  <ContactCardAttachment attachment={attachment} />
                ) : (
                  <FileAttachmentCard
                    attachment={attachment}
                    isFromMe={message.is_from_me}
                  />
                )}
              </div>
            </div>
          )
        })}

        {/* Message Status */}
        <div
          className=""
          style={{
            fontSize: '10px',
            opacity: 1,
            color: '#ffffff',
          }}
        >
          {message.is_from_me ? (
            <span
              style={{
                color: (() => {
                  const status = getStatusIcon(message)
                  return status === 'Not Delivered' || status === 'Paused'
                    ? '#ff3e2f'
                    : '#ffffff'
                })(),
              }}
            >
              <span style={{ fontWeight: 'bold' }}>
                {getStatusIcon(message)}
              </span>
              {' at '}
              {moment(message.sent_at).format('h:mm A')}
            </span>
          ) : (
            <span className="ms-1">
              {message.service} • {moment(message.sent_at).format('h:mm A')}
            </span>
          )}
        </div>
      </div>

      {/* Image Modal */}
      <ImageModal
        show={!!selectedImage}
        onHide={() => setSelectedImage(null)}
        imageUrl={selectedImage?.url || ''}
        filename={selectedImage?.filename}
      />
    </div>
  )
}

export default AttachmentMessageCard
